import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { fadeInUp } from '../../animations/animations';
import { SiC, SiLinux, SiPython, SiGitlab, SiGnubash } from 'react-icons/si';
import { MdTerminal, MdSettings, MdBugReport } from 'react-icons/md';

const About = () => {
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out',
    });
  }, []);

  const techStack = [
    { Icon: SiC,         name: 'Embedded C',  angle: 0   },
    { Icon: SiLinux,     name: 'Linux',        angle: 45  },
    { Icon: MdTerminal,  name: 'Buildroot',    angle: 90  },
    { Icon: MdSettings,  name: 'U-Boot',       angle: 135 },
    { Icon: SiPython,    name: 'Python',       angle: 180 },
    { Icon: MdBugReport, name: 'GDB',          angle: 225 },
    { Icon: SiGitlab,    name: 'GitLab CI',    angle: 270 },
    { Icon: SiGnubash,   name: 'Bash',         angle: 315 },
  ];

  const containerSize = 288;
  const radius = 110;
  const iconSize = 56;

  return (
    <section id="about" className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <motion.h2
          variants={fadeInUp}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
          className="section-header"
        >
          About Me
        </motion.h2>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div data-aos="fade-right">
            <p className="text-lg text-textPrimary mb-6 leading-relaxed">
              I am an <span className="text-primary font-semibold">Embedded Linux &amp; Firmware Engineer</span>.
              My core expertise is low-level software on ARM-based platforms - from cross-compiler
              toolchains and custom rootfs bring-up to kernel-level debugging and production OTA systems.
              I think in hardware and software together, which shapes how I approach every reliability
              and performance problem.
            </p>

            <p className="text-lg text-textPrimary mb-6 leading-relaxed">
              Everything I know came from building production systems, breaking them, and figuring out
              why. I have reverse engineered proprietary OEM firmware with no vendor documentation,
              worked around locked bootloaders using Linux namespaces, and built a
              <span className="text-primary font-semibold"> secure OTA updater in C</span> with
              AES-256-CBC + RSA-2048 from scratch.
            </p>

            <p className="text-textSecondary leading-relaxed">
              I care about the engineering environment as much as the code itself. A broken development
              toolchain or a slow CI pipeline costs the same as a bug in production.
            </p>

            {/* Gradient Line Decoration */}
            <div className="mt-8 flex items-center gap-4">
              <div className="gradient-line" />
              <span className="text-textMuted text-sm">
                Based in Bengaluru, India
              </span>
            </div>
          </div>

          {/* Rotating Tech Stack */}
          <div className="flex justify-center items-center" data-aos="fade-left">
            <div className="relative" style={{ width: containerSize, height: containerSize }}>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 60, ease: 'linear', repeat: Infinity }}
                className="absolute inset-0 dashed-circle rounded-full"
              >
                {techStack.map((tech, index) => {
                  const angleRad = (tech.angle - 90) * (Math.PI / 180);
                  const x = Math.cos(angleRad) * radius + (containerSize / 2) - (iconSize / 2);
                  const y = Math.sin(angleRad) * radius + (containerSize / 2) - (iconSize / 2);

                  return (
                    <motion.div
                      key={index}
                      animate={{ rotate: -360 }}
                      transition={{ duration: 60, ease: 'linear', repeat: Infinity }}
                      className="absolute w-14 h-14 flex items-center justify-center rounded-full bg-darkSurface border border-white/10 hover:border-primary hover:bg-primary/10 transition-all"
                      style={{ left: x, top: y }}
                      title={tech.name}
                    >
                      <tech.Icon className="w-7 h-7 text-primaryLight" />
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Center Icon */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-darkSurface border-2 border-primary rounded-full flex items-center justify-center glow-primary z-10">
                <span className="text-2xl font-bold gradient-text">SP</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
